/**
 * CycleContextBar — Persistent Focus Mode Header
 *
 * Appears on every page when the user has entered a focus mode (cycle, track,
 * session, or tool). Provides:
 *
 *   LEFT  — Context breadcrumb: Cycle > Track > Session (deepens as focus narrows)
 *   CENTER — Scoped nav tabs for the active cycle (always cycle-level)
 *   RIGHT  — Exit focus mode
 *
 * Design: single row, bg-[var(--bg-raised)], underline tab indicators. Warm dark, not stark.
 * The bar's ground is a token, so its words are tokens too: `text-white` here was
 * white on white in light mode.
 */

import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  RefreshCcw,
  Layers,
  Target,
  ArrowUpDown,
  ScrollText,
  BadgeCheck,
  X,
  ListOrdered,
} from 'lucide-react';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useFocusModeBarAligned } from '../../hooks/prioritization/useFocusModeBarAligned';

function cycleNavLinks(cycleId: string) {
  return [
    { label: 'Overview',     href: `/prioritization/cycles/${cycleId}`,                           icon: RefreshCcw  },
    { label: 'Tracks',       href: `/prioritization/tracks?cycleId=${cycleId}`,                   icon: Layers      },
    { label: 'Sessions',     href: `/prioritization/sessions?cycleId=${cycleId}`,                 icon: Target      },
    { label: 'Catchball',    href: `/prioritization/cascade/cycle/${cycleId}`,                   icon: ArrowUpDown },
    { label: 'Proposals',    href: `/prioritization/governance/proposals?cycleId=${cycleId}`,     icon: ScrollText  },
    { label: 'Ratification', href: `/prioritization/governance/ratification?cycleId=${cycleId}`,  icon: BadgeCheck  },
    { label: 'Results',      href: `/prioritization/cycles/${cycleId}/results`,                    icon: ListOrdered },
  ] as const;
}

export function CycleContextBar() {
  const pinnedCycleId    = usePinnedCycleStore((s) => s.pinnedCycleId);
  const pinnedCycleName  = usePinnedCycleStore((s) => s.pinnedCycleName);
  const pinnedTrackId    = usePinnedCycleStore((s) => s.pinnedTrackId);
  const pinnedTrackName  = usePinnedCycleStore((s) => s.pinnedTrackName);
  const pinnedSessionId  = usePinnedCycleStore((s) => s.pinnedSessionId);
  const pinnedSessionName = usePinnedCycleStore((s) => s.pinnedSessionName);
  const exitAll          = usePinnedCycleStore((s) => s.exitAll);
  const exitTrack        = usePinnedCycleStore((s) => s.exitTrack);
  const exitSession      = usePinnedCycleStore((s) => s.exitSession);
  const location         = useLocation();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();

  // Strip cycle-related URL params and clear store state
  const handleExitAll = () => {
    exitAll();
    const next = new URLSearchParams(searchParams);
    next.delete('cycleId');
    next.delete('trackId');
    next.delete('sessionId');
    const qs = next.toString();
    navigate(location.pathname + (qs ? `?${qs}` : ''), { replace: true });
  };

  const handleExitTrack = () => {
    exitTrack();
    const next = new URLSearchParams(searchParams);
    next.delete('trackId');
    next.delete('sessionId');
    const qs = next.toString();
    navigate(location.pathname + (qs ? `?${qs}` : ''), { replace: true });
  };

  const handleExitSession = () => {
    exitSession();
    const next = new URLSearchParams(searchParams);
    next.delete('sessionId');
    const qs = next.toString();
    navigate(location.pathname + (qs ? `?${qs}` : ''), { replace: true });
  };

  // Resolve cycle name from store if not denormalised
  const cycles      = useDegapProcessStore((s) => s.cycles);
  const cycleName   = pinnedCycleName ?? cycles.find((c) => c.id === pinnedCycleId)?.name ?? 'Cycle';

  const showBar = useFocusModeBarAligned();
  if (!pinnedCycleId || !showBar) return null;

  const links = cycleNavLinks(pinnedCycleId);

  return (
    <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] flex-shrink-0">
      <div className="flex items-stretch h-11 overflow-x-auto">

        {/* ── Context breadcrumb ──────────────────────────────────────────── */}
        <div className="flex items-center gap-1.5 pl-6 pr-5 border-r border-[var(--rule)] flex-shrink-0 min-w-0">
          {/* Cycle level */}
          <span className="h-2 w-2 rounded-full bg-[var(--mustard)] flex-shrink-0" />
          <Link
            to={`/prioritization/cycles/${pinnedCycleId}`}
            className="text-sm font-semibold text-[var(--text-pri)] hover:text-[var(--mustard)] transition-colors truncate max-w-[180px]"
            title={cycleName}
          >
            {cycleName}
          </Link>

          {/* Track level */}
          {pinnedTrackId && pinnedTrackName && (
            <>
              <ChevronRight className="h-3.5 w-3.5 text-[var(--text-sec)] flex-shrink-0" />
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
              <button
                onClick={handleExitSession}
                className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors truncate max-w-[140px] text-left"
                title={`${pinnedTrackName} — click to drop session focus`}
              >
                {pinnedTrackName}
              </button>
            </>
          )}

          {/* Session level */}
          {pinnedSessionId && pinnedSessionName && (
            <>
              <ChevronRight className="h-3.5 w-3.5 text-[var(--text-sec)] flex-shrink-0" />
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] flex-shrink-0" />
              <span
                className="text-sm text-[var(--text-ter)] truncate max-w-[140px]"
                title={pinnedSessionName}
              >
                {pinnedSessionName}
              </span>
            </>
          )}
        </div>

        {/* ── Scoped nav tabs ─────────────────────────────────────────────── */}
        <div className="flex items-stretch flex-1 ml-2">
          {links.map(({ label, href, icon: Icon }) => {
            const linkPath = href.split('?')[0];
            const isActive =
              location.pathname === linkPath ||
              location.pathname.startsWith(linkPath + '/');

            return (
              <Link
                key={label}
                to={href}
                className={`
                  relative flex items-center gap-1.5 px-4 text-xs font-medium
                  whitespace-nowrap transition-colors
                  ${isActive
                    ? 'text-[var(--text-pri)]'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-pri)]'
                  }
                `}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                {label}
                {isActive && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[var(--mustard)] rounded-t-full" />
                )}
              </Link>
            );
          })}
        </div>

        {/* ── Exit ────────────────────────────────────────────────────────── */}
        <div className="flex items-center px-4 border-l border-[var(--rule)] flex-shrink-0">
          {/* Offer narrower exits when deeper context is active */}
          {pinnedTrackId && !pinnedSessionId && (
            <button
              type="button"
              onClick={handleExitTrack}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors mr-3"
              title="Drop Track focus, keep cycle"
            >
              ↑ cycle only
            </button>
          )}
          {pinnedSessionId && (
            <button
              type="button"
              onClick={handleExitSession}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors mr-3"
              title="Drop session focus, keep Track & cycle"
            >
              ↑ track only
            </button>
          )}
          <button
            type="button"
            onClick={handleExitAll}
            className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors"
            title="Exit focus mode entirely"
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </button>
        </div>

      </div>
    </div>
  );
}

export default CycleContextBar;
